import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { ConferenceRoom } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';

// Drop zero/empty seating layouts so saving doesn't inject e.g. uShape:0, boardroom:0
function cleanLayouts(layouts: Record<string, number>): Record<string, number> {
  return Object.fromEntries(Object.entries(layouts || {}).filter(([, v]) => Number(v) > 0));
}

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function ConferenceRoomsList() {
  const { data, isLoading, error } = useList<ConferenceRoom>({ resource: 'conference-rooms' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this conference room?')) {
      deleteOne({ resource: 'conference-rooms', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading conference rooms: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Conference Rooms</h1>
        <button onClick={() => create('conference-rooms')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Room
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((room) => (
              <tr key={room.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{room.name}</div>
                  <div className="text-sm text-gray-500">{room.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{room.capacity} pax</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${room.venueType === 'outdoor' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {room.venueType === 'outdoor' ? 'Outdoor' : 'Indoor'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{room.size ? `${room.size} m²` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('conference-rooms', room.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(room.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No conference rooms found. Click "Add Room" to create one.</div>}
      </div>
    </div>
  );
}

// Create Conference Room
export function ConferenceRoomsCreate() {
  const navigate = useNavigate();
  const { mutate: createRoom, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    capacity: 0,
    size: 0,
    venueType: 'indoor' as string,
    amenities: [] as string[],
    layouts: { theater: 0, classroom: 0, uShape: 0, boardroom: 0 },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    const slug = form.slug || generateSlug(form.nameId);
    createRoom(
      { resource: 'conference-rooms', values: { ...form, slug, layouts: cleanLayouts(form.layouts), name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/conference-rooms/edit/${newId}` : '/conference-rooms'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/conference-rooms')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Conference Room</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new meeting room or conference facility</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
              <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Size (m²)</label>
              <input type="number" value={form.size} onChange={(e) => setForm({ ...form, size: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue Type</label>
              <select value={form.venueType} onChange={(e) => setForm({ ...form, venueType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add images and more details after creating the room.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/conference-rooms')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Conference Room
export function ConferenceRoomsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<ConferenceRoom>({ resource: 'conference-rooms', id: id! });
  const { mutate: updateRoom, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    capacity: 0,
    size: 0,
    venueType: 'indoor' as string,
    amenities: [] as string[],
    layouts: { theater: 0, classroom: 0, uShape: 0, boardroom: 0 },
  });
  const [images, setImages] = useState<ManagedImage[]>([]);
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    if (data?.data) {
      const room = data.data;
      const defaultLayouts = { theater: 0, classroom: 0, uShape: 0, boardroom: 0 };
      setForm({
        slug: room.slug || '',
        nameEn: (room as any).nameEn || (room as any).name_en || room.name || '',
        nameId: (room as any).nameId || (room as any).name_id || room.name || '',
        descriptionEn: (room as any).descriptionEn || (room as any).description_en || room.description || '',
        descriptionId: (room as any).descriptionId || (room as any).description_id || room.description || '',
        capacity: room.capacity || 0,
        size: room.size || 0,
        venueType: room.venueType || 'indoor',
        amenities: room.amenities || [],
        layouts: { ...defaultLayouts, ...room.layouts },
      });
      if (room.images) {
        setImages(room.images.map((url, index) => ({ url, imageType: 'gallery' as const, sortOrder: index })));
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateRoom(
      { resource: 'conference-rooms', id: id!, values: { ...form, layouts: cleanLayouts(form.layouts), images: images.map((img) => img.url) } },
      { onSuccess: () => navigate('/conference-rooms') }
    );
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setForm({ ...form, amenities: [...form.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };

  const removeAmenity = (index: number) => {
    setForm({ ...form, amenities: form.amenities.filter((_, i) => i !== index) });
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/conference-rooms')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Conference Room</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Room details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>
            <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Room Details</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity (pax)</label>
                <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Size (m²)</label>
                <input type="number" value={form.size} onChange={(e) => setForm({ ...form, size: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Type</label>
                <select value={form.venueType} onChange={(e) => setForm({ ...form, venueType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                  <option value="indoor">Indoor</option>
                  <option value="outdoor">Outdoor</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Seating Layouts (Capacity)</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Theater Style</label>
                <input type="number" value={form.layouts.theater} onChange={(e) => setForm({ ...form, layouts: { ...form.layouts, theater: parseInt(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classroom</label>
                <input type="number" value={form.layouts.classroom} onChange={(e) => setForm({ ...form, layouts: { ...form.layouts, classroom: parseInt(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">U-Shape</label>
                <input type="number" value={form.layouts.uShape} onChange={(e) => setForm({ ...form, layouts: { ...form.layouts, uShape: parseInt(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Boardroom</label>
                <input type="number" value={form.layouts.boardroom} onChange={(e) => setForm({ ...form, layouts: { ...form.layouts, boardroom: parseInt(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Amenities</h2>
            <div className="flex gap-2 mb-2">
              <input type="text" value={newAmenity} onChange={(e) => setNewAmenity(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())} placeholder="Add amenity..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" />
              <button type="button" onClick={addAmenity} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.amenities.map((item, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                  {item}
                  <button type="button" onClick={() => removeAmenity(index)} className="text-blue-600 hover:text-blue-800">x</button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/conference-rooms')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Room Images</h2>
            <ImageManager entityType="conference" entityId={id!} images={images} imageType="gallery" multiple={true} maxImages={10} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
